/**
 * Lease Button Handler
 * 
 * Responsibilities:
 * - Radio group setup (lease type, lease term)
 * - Lease term visibility based on lease type
 * - Button text/URL switching (prive/zakelijk)
 * - Bestelcode button visibility
 * - Save product data to sessionStorage on navigation
 * 
 */

(function() {
  'use strict';
  
  const DEBUG = false;
  
  function debugLog(...args) {
    if (DEBUG) console.log(...args);
  }
  
  /**
   * Sanitize string to prevent XSS when displaying data
   */
  function sanitizeString(str) {
    if (str === null || str === undefined) return '';
    if (typeof str !== 'string') str = String(str);
    return str.replace(/[<>"'&]/g, (char) => {
      const entities = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
        '&': '&amp;'
      };
      return entities[char];
    });
  }
  
  /**
   * Validate URL to prevent javascript: injection
   */
  function isValidUrl(url) {
    if (!url || typeof url !== 'string') return false;
    
    const trimmedUrl = url.trim();
    
    // Block dangerous protocols
    if (/^(javascript|data|vbscript):/i.test(trimmedUrl)) {
      return false;
    }
    
    // Allow relative URLs starting with /
    if (trimmedUrl.startsWith('/')) return true;
    
    try {
      const parsed = new URL(trimmedUrl, window.location.origin);
      // Only allow same origin
      return parsed.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Validate and parse number
   */
  function parseValidNumber(value, min = 0, max = Infinity) {
    const num = parseFloat(value);
    if (typeof num !== 'number' || !isFinite(num) || isNaN(num)) {
      return null;
    }
    if (num < min || num > max) {
      return null;
    }
    return num;
  }
  
  /**
   * Validate image URL
   */
  function validateImageUrl(src) {
    if (!src || typeof src !== 'string') return '';
    
    try {
      const imgUrl = new URL(src);
      // Only allow http(s) protocols
      if (imgUrl.protocol === 'https:' || imgUrl.protocol === 'http:') {
        return src;
      }
    } catch (e) {
      debugLog('Invalid image URL');
    }
    return '';
  }
  
  document.addEventListener('DOMContentLoaded', function() {
    
    // ========================================
    // RADIO GROUP SETUP
    // ========================================
    
    /**
     * Setup radio group to ensure proper toggle behavior
     */
    function setupRadioGroup(name) {
      const radios = document.querySelectorAll('input[name="' + name + '"]');
      radios.forEach(radio => {
        if (radio.type !== 'radio') return; // Validate element type
        
        radio.addEventListener('click', function() {
          radios.forEach(r => r.checked = false);
          this.checked = true;
        });
      });
    }
    
    /**
     * Handle lease term visibility based on lease type
     * - Zakelijk: only 36 months
     * - Prive: 36 and 48 months
     */
    function setupLeaseTermVisibility() {
      const zakelijkRadio = document.querySelector('input[name="lease_type"][value="zakelijk"]');
      const priveRadio = document.querySelector('input[name="lease_type"][value="prive"]');
      const term36Radio = document.querySelector('input[name="lease_term"][value="36"]');
      const term48Radio = document.querySelector('input[name="lease_term"][value="48"]');
      
      if (!zakelijkRadio || !term36Radio || !term48Radio) return;
      
      // Validate element types
      if (zakelijkRadio.type !== 'radio' || term36Radio.type !== 'radio' || term48Radio.type !== 'radio') {
        return;
      }
      
      const term48Label = document.querySelector('label[for="' + term48Radio.id + '"]');
      
      function updateTermVisibility() {
        if (zakelijkRadio.checked) {
          // Zakelijk: hide 48 months, select 36
          term48Radio.style.display = 'none';
          if (term48Label) term48Label.style.display = 'none';
          term36Radio.checked = true;
          term36Radio.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          // Prive: show both options
          term48Radio.style.display = '';
          if (term48Label) term48Label.style.display = '';
        }
      }
      
      zakelijkRadio.addEventListener('change', updateTermVisibility);
      if (priveRadio && priveRadio.type === 'radio') {
        priveRadio.addEventListener('change', updateTermVisibility);
      }
      
      // Initial setup
      updateTermVisibility();
    }
    
    // Initialize radio groups if they exist
    if (document.querySelector('input[name="lease_type"]')) {
      setupRadioGroup('lease_type');
    }
    
    if (document.querySelector('input[name="lease_term"]')) {
      setupRadioGroup('lease_term');
    }
    
    setupLeaseTermVisibility();
    
    // ========================================
    // LEASE BUTTON HANDLER
    // ========================================
    
    const leaseButton = document.getElementById('lease-action-button');
    const leasePriceDisplay = document.getElementById('lease-price-display');
    const leaseTypeRadios = document.querySelectorAll('input[name="lease_type"]');
    const bestelcodeButton = document.getElementById('bestelcode-button');
    
    // Exit if no lease button on page
    if (!leaseButton) return;
    
    const buttonText = leaseButton.querySelector('.button-text');
    let intervalId = null;
    
    /**
     * Update lease button text, URL, and disabled state
     */
    function updateLeaseButton() {
      // Check if element still exists in DOM
      if (!document.body.contains(leaseButton)) {
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
        return;
      }
      
      const selectedType = document.querySelector('input[name="lease_type"]:checked');
      const leaseType = selectedType ? selectedType.value : 'prive';
      
      // Validate lease type
      if (leaseType !== 'prive' && leaseType !== 'zakelijk') {
        debugLog('Invalid lease type detected');
        return;
      }
      
      // Update button text and URL
      if (buttonText) {
        if (leaseType === 'prive') {
          buttonText.textContent = leaseButton.dataset.priveText || '';
          leaseButton.dataset.currentUrl = leaseButton.dataset.priveUrl || '';
        } else {
          buttonText.textContent = leaseButton.dataset.zakelijkText || '';
          leaseButton.dataset.currentUrl = leaseButton.dataset.zakelijkUrl || '';
        }
      }
      
      // Show/hide bestelcode button
      if (bestelcodeButton) {
        if (leaseType === 'zakelijk') {
          bestelcodeButton.classList.add('is-visible');
        } else {
          bestelcodeButton.classList.remove('is-visible');
        }
      }

      // Show/hide info tooltips based on lease type
      var priveTooltips = document.querySelectorAll('.info-tooltip-prive');
      var zakelijkTooltips = document.querySelectorAll('.info-tooltip-zakelijk');

      priveTooltips.forEach(function(tooltip) {
        tooltip.style.display = leaseType === 'prive' ? 'inline-block' : 'none';
      });

      zakelijkTooltips.forEach(function(tooltip) {
        tooltip.style.display = leaseType === 'zakelijk' ? 'inline-block' : 'none';
      });
      
      // Update disabled state based on price
      const hasValidPrice = checkLeasePrice();
      leaseButton.disabled = !hasValidPrice;
    }
    
    /**
     * Check if there's a valid lease price displayed.
     * Reads from dataset (set by lease-calculator) rather than parsing text,
     * because the display can contain both a compare-at (struck-through) price
     * and a current price.
     */
    function checkLeasePrice() {
      if (!leasePriceDisplay) return false;
      if (leasePriceDisplay.classList.contains('hidden')) return false;

      var current = leasePriceDisplay.dataset.currentLeasePrice;
      if (current) {
        var price = parseValidNumber(current, 0, 100000);
        return price !== null && price > 0;
      }

      // Fallback: parse from text if dataset is not set
      const priceText = leasePriceDisplay.textContent || '';
      const priceMatch = priceText.match(/[\d,.]+/);

      if (priceMatch) {
        const price = parseValidNumber(priceMatch[0].replace(',', '.'), 0, 100000);
        return price !== null && price > 0;
      }

      return false;
    }
    
    /**
     * Get spec value from specs table
     */
    function getSpecValue(specName) {
      const rows = document.querySelectorAll('.specs-table .specs-row');
      for (const row of rows) {
        const label = row.querySelector('td:first-child');
        if (label && label.textContent.trim() === specName) {
          const value = row.querySelector('td:last-child');
          return value ? value.textContent.trim() : '';
        }
      }
      return '';
    }
    
    /**
     * Save product data to sessionStorage before navigation
     */
    function saveProductDataToSession() {
      // Get lease options
      const leaseTypeInput = document.querySelector('input[name="lease_type"]:checked');
      const leaseTermInput = document.querySelector('input[name="lease_term"]:checked');
      
      // Get variant options
      const sizeInput = document.querySelector('input[name^="Maat"]:checked');
      const colorInput = document.querySelector('input[name^="Kleur"]:checked');
      const accuInput = document.querySelector('input[name^="Accu"]:checked');
    
      let variantPrice = '';

      // Probeer eerst uit Accu input
      if (accuInput && accuInput.dataset.variantPrice) {
        variantPrice = (accuInput.dataset.variantPrice / 100).toFixed(2).replace('.', ',');
      } else {
        // Fallback: haal prijs uit variant script
        const variantDataScript = document.querySelector('script[data-selected-variant]');
        if (variantDataScript) {
          try {
            const variantData = JSON.parse(variantDataScript.textContent);
            if (variantData && variantData.price) {
              variantPrice = (variantData.price / 100).toFixed(2).replace('.', ',');
            }
          } catch (e) {
            debugLog('Error parsing variant price:', e);
          }
        }
      }

      // Get lease price (prefer dataset set by lease-calculator, fallback to text)
      let leasePrice = '';
      if (leasePriceDisplay) {
        const datasetPrice = leasePriceDisplay.dataset.currentLeasePrice;
        if (datasetPrice) {
          // Format with comma as decimal separator to stay consistent
          leasePrice = String(datasetPrice).replace('.', ',');
        } else {
          const priceText = leasePriceDisplay.textContent || '';
          const priceMatch = priceText.match(/[\d,.]+/);
          if (priceMatch) {
            leasePrice = priceMatch[0];
          }
        }
      }
      
      // Get variant ID from JSON data
      const variantDataScript = document.querySelector('script[data-selected-variant]');
      let variantId = '';
      let variantTitle = '';
      if (variantDataScript) {
        try {
          const variantData = JSON.parse(variantDataScript.textContent);
          if (variantData && typeof variantData.id === 'number') {
            variantId = String(variantData.id);
          }
          if (variantData && typeof variantData.title === 'string') {
            variantTitle = variantData.title;
          }
        } catch (e) {
          debugLog('Error parsing variant data:', e);
        }
      }
      
      // Fallback: try to get variant ID from product form input
      if (!variantId) {
        const variantInput = document.querySelector('input[name="id"]');
        if (variantInput && variantInput.value) {
          variantId = String(variantInput.value);
          debugLog('Variant ID from form input:', variantId);
        }
      }
      
      // Fallback: try ShopifyAnalytics for products without variants
      if (!variantId) {
        try {
          if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && 
              window.ShopifyAnalytics.meta.product && 
              window.ShopifyAnalytics.meta.product.variants &&
              window.ShopifyAnalytics.meta.product.variants[0]) {
            variantId = String(window.ShopifyAnalytics.meta.product.variants[0].id);
            debugLog('Variant ID from ShopifyAnalytics:', variantId);
          }
        } catch (e) {
          debugLog('Error accessing ShopifyAnalytics:', e);
        }
      }
      
      // Get variant title if not set
      if (!variantTitle) {
        const variantTitleInput = document.querySelector('input[name="contact[Variant]"]');
        if (variantTitleInput && variantTitleInput.value) {
          variantTitle = variantTitleInput.value;
        }
      }
      
      // Get product title
      const productTitleEl = document.querySelector('.product__title');
      const productTitle = productTitleEl ? productTitleEl.textContent.trim() : '';
      
      // Get product image
      const productImage = document.querySelector('.product__media img');
      const productImageUrl = productImage ? validateImageUrl(productImage.src) : '';
      
      // Build sanitized data object
      const productData = {
        leaseType: sanitizeString(leaseTypeInput ? leaseTypeInput.value : 'prive'),
        leaseTerm: sanitizeString(leaseTermInput ? leaseTermInput.value : '48'),
        maat: sanitizeString(sizeInput ? sizeInput.value : 'One Size'),
        kleur: sanitizeString(colorInput ? colorInput.value : 'Uni'),
        accu: sanitizeString(accuInput ? accuInput.value : 'n.v.t.'),
        leasePricePerMonth: sanitizeString(leasePrice),
        totaalPrijs: sanitizeString(variantPrice),
        variantId: sanitizeString(variantId),
        variantTitle: sanitizeString(variantTitle),
        productTitle: sanitizeString(productTitle),
        productImageUrl: sanitizeString(productImageUrl),
    //    merk: sanitizeString(getSpecValue('Merk')),
    //    fietstype: sanitizeString(getSpecValue('Fietstype')),
    //    frametype: sanitizeString(getSpecValue('Frametype')),
    //    motorpositie: sanitizeString(getSpecValue('Motorpositie')),
    //    motortype: sanitizeString(getSpecValue('Motortype')),
    //    accupositie: sanitizeString(getSpecValue('Accupositie')),
    //    gewicht: sanitizeString(getSpecValue('Gewicht')),
    //    aandrijving: sanitizeString(getSpecValue('Aandrijving')),
    //    motor: sanitizeString(getSpecValue('Motor')),
    //    koppel: sanitizeString(getSpecValue('Koppel')),
    //    remsysteem: sanitizeString(getSpecValue('Remsysteem')),
        versnellingen: sanitizeString(getSpecValue('Versnellingen')),
        savedAt: new Date().toISOString()
      };
      
      // Save to sessionStorage
      try {
        sessionStorage.setItem('leaseProductData', JSON.stringify(productData));
        debugLog('Product data saved:', productData);
      } catch (e) {
        debugLog('Error saving to sessionStorage:', e);
      }
      
      return productData;
    }
    
    /**
     * Handle lease button click
     */
    function handleLeaseButtonClick() {
      if (leaseButton.disabled) return;
      
      // Save data before navigation
      saveProductDataToSession();
      
      const targetUrl = leaseButton.dataset.currentUrl;
      if (targetUrl && isValidUrl(targetUrl)) {
        window.location.href = targetUrl;
      } else {
        debugLog('Invalid or missing URL');
      }
    }
    
    /**
     * Handle bestelcode button click
     */
    function handleBestelcodeButtonClick(e) {
      e.preventDefault();
      
      // Save data before navigation
      saveProductDataToSession();
      
      const targetUrl = bestelcodeButton.getAttribute('href');
      if (targetUrl && isValidUrl(targetUrl)) {
        window.location.href = targetUrl;
      } else {
        debugLog('Invalid or missing bestelcode URL');
      }
    }
    
    // ========================================
    // EVENT LISTENERS
    // ========================================
    
    // Lease type change
    leaseTypeRadios.forEach(radio => {
      radio.addEventListener('change', updateLeaseButton);
    });
    
    // Lease button click
    leaseButton.addEventListener('click', handleLeaseButtonClick);
    
    // Bestelcode button click
    if (bestelcodeButton) {
      bestelcodeButton.addEventListener('click', handleBestelcodeButtonClick);
    }
    
    // Observe lease price display for changes
    if (leasePriceDisplay) {
      const observer = new MutationObserver(function() {
        updateLeaseButton();
      });
      
      observer.observe(leasePriceDisplay, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }
    
    // Listen for variant changes
    document.addEventListener('variant:changed', updateLeaseButton);
    
    // Fallback: periodic check with cleanup
    intervalId = setInterval(function() {
      if (!document.body.contains(leaseButton)) {
        clearInterval(intervalId);
        intervalId = null;
        return;
      }
      updateLeaseButton();
    }, 500);
    
    // Initial update
    updateLeaseButton();
    
  });
})();